import { z } from 'zod';

/**
 * Teams are groups of maintenance staff inside a building (or org-wide
 * when `buildingId` is null — e.g. a roving on-call team). Membership is
 * many-to-many through `team_members`, with `is_lead` flagging members
 * who can re-assign within their own team.
 */

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullish()
    .transform((v) => v ?? null);

export const createTeamInput = z.object({
  name: z.string().trim().min(1).max(120),
  description: optionalText(2_000),
  buildingId: z.string().uuid().nullish().transform((v) => v ?? null),
  members: z
    .array(
      z.object({
        userId: z.string().uuid(),
        isLead: z.boolean().default(false),
      }),
    )
    .max(200)
    .optional()
    .default([]),
});
export type CreateTeamInput = z.infer<typeof createTeamInput>;

export const updateTeamInput = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: optionalText(2_000).optional(),
    buildingId: z.string().uuid().nullish().transform((v) => v ?? null).optional(),
  })
  .refine(
    (v) => Object.values(v).some((x) => x !== undefined),
    { message: 'Provide at least one field to update' },
  );
export type UpdateTeamInput = z.infer<typeof updateTeamInput>;

export const addTeamMemberInput = z.object({
  userId: z.string().uuid(),
  isLead: z.boolean().default(false),
});
export type AddTeamMemberInput = z.infer<typeof addTeamMemberInput>;

export const updateTeamMemberInput = z.object({
  isLead: z.boolean(),
});
export type UpdateTeamMemberInput = z.infer<typeof updateTeamMemberInput>;
